import { useMemo, useState } from "react";
import "./TestCard.css";
import { GestionHeader } from "./SiteHeader";
import WalletPreview from "./WalletPreview";
import { ApiError } from "./api";

/**
 * Banc d'essai « Test carte » (admin) : génère des pass Apple Wallet
 * expérimentaux de qualité concurrent, sans toucher aux cartes réelles.
 * Le pass produit est éphémère (2 h), à scanner avec un iPhone de test.
 */
function TestCard({ user, onNavigate, onLogout }) {
  const [platform, setPlatform] = useState("google");
  const [orgName, setOrgName] = useState("Les Gourmandises");
  const [color, setColor] = useState("#cbb59a");
  const [style, setStyle] = useState("kraft");
  const [mode, setMode] = useState("stamps");
  const [stamps, setStamps] = useState(6);
  const [points, setPoints] = useState(120);
  const [niveau, setNiveau] = useState("Coupon de 10€ pour le prochain achat");
  const [logo, setLogo] = useState(null);
  const [photo, setPhoto] = useState(null);
  const [result, setResult] = useState(null);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  // Strip (Apple) ou hero (Google) rendu par le backend ; les uploads
  // (logo, photo) sont prévisualisés côté client dans WalletPreview
  const previewUrl = useMemo(() => {
    const params = new URLSearchParams({
      style: style === "photo" ? "kraft" : style,
      color,
      stamps: String(mode === "stamps" ? stamps : 0),
      mode,
      org_name: orgName || "Test",
      platform,
    });
    return `/api/test-cards/preview.png?${params}`;
  }, [style, color, stamps, mode, orgName, platform]);

  const generate = async () => {
    setBusy(true);
    setError("");
    setResult(null);
    try {
      const form = new FormData();
      form.append("platform", platform);
      form.append("org_name", orgName);
      form.append("color", color);
      form.append("style", style);
      form.append("mode", mode);
      form.append("stamps", String(stamps));
      form.append("points", String(points));
      form.append("niveau", niveau);
      if (logo) form.append("logo", logo);
      if (photo) form.append("photo", photo);
      const res = await fetch("/api/test-cards/generate", { method: "POST", body: form });
      const data = await res.json().catch(() => null);
      if (!res.ok) {
        throw new ApiError(
          (data && typeof data.detail === "string" && data.detail) || "Échec de la génération",
          res.status
        );
      }
      setResult(data);
    } catch (e) {
      setError(e instanceof ApiError ? e.message : "Échec de la génération");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="admin-container">
      <GestionHeader
        user={user}
        onNavigate={onNavigate}
        title="Test carte"
        onBack={() => onNavigate("admin")}
        onHome={() => onNavigate("home")}
        onLogout={onLogout}
      />
      <main className="testcard-main">
        <h2>Banc d'essai — cartes nouvelle génération</h2>
        <p className="testcard-hint">
          Pass Apple Wallet expérimentaux : aucune carte réelle n'est créée ni
          modifiée. Le pass généré expire au bout de 2&nbsp;heures.
        </p>

        <div className="testcard-grid">
          <section className="testcard-form">
            <fieldset>
              <legend>Plateforme</legend>
              <label className="testcard-radio">
                <input type="radio" checked={platform === "google"}
                       onChange={() => { setPlatform("google"); if (style === "fullbg") setStyle("kraft"); }} />
                Android — Google Wallet
              </label>
              <label className="testcard-radio">
                <input type="radio" checked={platform === "apple"}
                       onChange={() => setPlatform("apple")} />
                iPhone — Apple Wallet <small>(.pkpass)</small>
              </label>
            </fieldset>

            <label>
              Nom du commerce
              <input value={orgName} maxLength={40}
                     onChange={(e) => setOrgName(e.target.value)} />
            </label>

            <label>
              Couleur de fond
              <span className="testcard-colorrow">
                <input type="color" value={color}
                       onChange={(e) => setColor(e.target.value)} />
                <code>{color}</code>
              </span>
            </label>

            <fieldset>
              <legend>Style</legend>
              <label className="testcard-radio">
                <input type="radio" checked={style === "kraft"}
                       onChange={() => setStyle("kraft")} />
                Kraft + tampons <small>(réplique concurrent, storeCard)</small>
              </label>
              <label className="testcard-radio">
                <input type="radio" checked={style === "photo"}
                       onChange={() => setStyle("photo")} />
                Photo du commerce <small>(bandeau photo, storeCard)</small>
              </label>
              <label className="testcard-radio">
                <input type="radio" checked={style === "fullbg"}
                       disabled={platform === "google"}
                       onChange={() => setStyle("fullbg")} />
                Fond intégral <small>
                  {platform === "google"
                    ? "(Apple uniquement — Google impose sa mise en page)"
                    : "(eventTicket, flouté par iOS — à évaluer)"}
                </small>
              </label>
            </fieldset>

            <fieldset>
              <legend>Compteur</legend>
              <label className="testcard-radio">
                <input type="radio" checked={mode === "stamps"}
                       onChange={() => setMode("stamps")} />
                Tampons
                {mode === "stamps" && (
                  <input type="number" min={0} max={10} value={stamps}
                         className="testcard-num"
                         onChange={(e) => setStamps(Number(e.target.value))} />
                )}
              </label>
              <label className="testcard-radio">
                <input type="radio" checked={mode === "points"}
                       onChange={() => setMode("points")} />
                Points
                {mode === "points" && (
                  <input type="number" min={0} value={points}
                         className="testcard-num"
                         onChange={(e) => setPoints(Number(e.target.value))} />
                )}
              </label>
            </fieldset>

            <label>
              Récompense <small>(optionnel — sur Apple entre les tampons et le
              QR, sur Google dans les détails)</small>
              <input value={niveau} maxLength={60}
                     onChange={(e) => setNiveau(e.target.value)} />
            </label>

            <label>
              Logo <small>(optionnel, pastille ronde en haut de la carte)</small>
              <input type="file" accept="image/*"
                     onChange={(e) => setLogo(e.target.files[0] || null)} />
            </label>

            <label>
              Photo du commerce{" "}
              <small>
                {style === "photo" && mode === "stamps"
                  ? "(indisponible en mode tampons : la grille prend la place)"
                  : "(requise pour le style photo, mode points)"}
              </small>
              <input type="file" accept="image/*"
                     disabled={style === "photo" && mode === "stamps"}
                     onChange={(e) => setPhoto(e.target.files[0] || null)} />
            </label>

            <button className="testcard-generate" onClick={generate}
                    disabled={busy || (style === "photo" && mode !== "stamps" && !photo)}>
              {busy ? "Génération…" : "Générer le pass de test"}
            </button>
            {error && <p className="testcard-error">{error}</p>}
          </section>

          <section className="testcard-side">
            <h3>Aperçu du rendu attendu</h3>
            <WalletPreview
              platform={platform}
              orgName={orgName}
              color={color}
              cardStyle={style}
              mode={mode}
              stamps={stamps}
              points={points}
              niveau={niveau}
              logoFile={logo}
              photoFile={photo}
              stripUrl={previewUrl}
              testBanner
            />

            {result && (
              <div className="testcard-result">
                <h3>Pass {result.serial} prêt</h3>
                <p>
                  Scanne ce QR avec le téléphone
                  {result.platform === "google" ? " Android" : " iPhone"} de test&nbsp;:
                </p>
                <img className="testcard-qr" src={result.qr_url} alt="QR d'ajout du pass" />
                <p>
                  {result.platform === "google" ? (
                    <a href={result.save_url} target="_blank" rel="noreferrer">
                      Ou ouvre le lien «&nbsp;Enregistrer dans Google Wallet&nbsp;»
                    </a>
                  ) : (
                    <a href={result.download_url}>Ou télécharge le .pkpass directement</a>
                  )}
                  <br />
                  <small>
                    {result.platform === "google"
                      ? "Compte émetteur en mode démo : la carte portera le bandeau « TEST UNIQUEMENT », c'est normal."
                      : `Valable ${result.expires_minutes} minutes.`}
                  </small>
                </p>
              </div>
            )}
          </section>
        </div>
      </main>
    </div>
  );
}

export default TestCard;
